/**
 * Phase 6 cross-cutting PR 6 — `proactiveTypeReadable`.
 *
 * Maps a `ProactiveTriggerType` to the human-readable label that
 * surfaces in the motivation card's `RELATED · <label>` kicker when
 * a proactive trigger fired this load (per `direction.md §4`
 * PRESENTATION row 5).
 *
 * The kicker is the user-visible side of the cross-module handoff
 * shipped in PR 4f: useProactiveChecks → useMotivation LLM prompt
 * biasing. PR 4f made the daily motivation message theme around
 * the trigger; this PR adds the visual badge that says "this
 * message exists because <thing> happened" so the daughter can see
 * the connection.
 *
 * Pure function · no hooks · no React · trivially testable.
 */

import type { ProactiveTriggerType } from './proactiveCopy';

/** Mapping from internal trigger type to short user-facing label.
 *  Kept tight (1-3 words) so it fits the 11px-uppercase kicker
 *  alongside the "RELATED · " prefix without wrapping. */
const READABLE_LABELS: Record<ProactiveTriggerType, string> = {
  comeback: 'Comeback',
  compliance_drop: 'Plan compliance',
  race_countdown: 'Race countdown',
  streak_milestone: 'Streak milestone',
  pr_celebration: 'PR celebration',
  // NU-2 — the lifecycle trio can become primaryHint when no legacy
  // trigger fires the same load (it sorts LAST in PROACTIVE_PRIORITY).
  draft_next_week: 'Next week',
  unlogged_sessions: 'Session log',
  season_wrap: 'Season wrap',
};

/**
 * Returns the user-facing label for a proactive trigger type, or
 * null when the input is null/undefined (caller hides the kicker).
 *
 * Defensive default for unknown types: returns the raw type string
 * (with underscores replaced by spaces, title-cased). Should never
 * fire in production — `ProactiveTriggerType` is a discriminated
 * union — but covers any future enum-expansion gap gracefully.
 */
export function proactiveTypeReadable(
  type: ProactiveTriggerType | null | undefined,
): string | null {
  if (!type) return null;
  const known = READABLE_LABELS[type];
  if (known) return known;
  // Fallback — convert snake_case to Title Case.
  return (type as string)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
